/**
 * Store scope — the branch picker that lives in the top app bar.
 *
 * Only some pages are store-scoped (Live Orders first), so the picker declares
 * where it applies rather than every page inventing its own selector. On any
 * other route the picker hides itself and the scope is ignored.
 *
 * The selection is held here, not in the URL, so it survives navigating between
 * two scoped pages.
 */

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import authInstance from '../services/httpClient';
import { decodeToken, getStoredToken } from '../services/tokenRefreshService';
import Logger from '../utils/Logger';

/** "All stores" — no branch filter. */
export const ALL_STORES = 'all';

/** No branch selected yet — store-scoped pages should prompt the user to pick one. */
export const NO_STORE = 'none';
/**
 * Routes where the branch picker appears. Anywhere else it's hidden.
 *
 * NOTE: Live Orders and Orders actually filter on the selection. Uber Deliveries,
 * TFS Deliveries and Product Management currently have no store dimension to
 * filter on — the picker is shown there by request, but it won't narrow anything
 * until those pages (and their APIs) support a store filter.
 */
export const STORE_SCOPED_ROUTES = [
  '/live-orders',
  '/orders',
  '/uber-deliveries',
  '/tfs-deliveries',
  '/products',
];

export const isStoreScopedRoute = (pathname: string): boolean =>
  STORE_SCOPED_ROUTES.some((route) => pathname.startsWith(route));

/**
 * One picker option, from GET /admin/stores/options: the caller's active stores
 * (store roles get only their assigned ones), sorted by name.
 *
 * The selection is the store's `externalId` — the id orders carry in `storeId`
 * and the one `GET /admin/orders?storeId=` filters on — never its name, so a
 * renamed store can't silently stop matching.
 */
export interface StoreOption {
  id: string;
  externalId: string;
  name: string;
}

export type StoresStatus = 'idle' | 'loading' | 'ready' | 'error';

interface StoreScopeValue {
  /** Selected store externalId, ALL_STORES or NO_STORE. */
  store: string;
  setStore: (store: string) => void;
  /** The stores the signed-in user may pick, alphabetical. */
  stores: StoreOption[];
  storesStatus: StoresStatus;
  /** Load the options (no-op once loaded for this user; `force` retries). */
  requestStores: (force?: boolean) => void;
  /** True once the user has explicitly picked from the store picker. */
  touched: boolean;
}

const StoreScopeContext = createContext<StoreScopeValue>({
  store: NO_STORE,
  setStore: () => {},
  stores: [],
  storesStatus: 'idle',
  requestStores: () => {},
  touched: false,
});

export const useStoreScope = () => useContext(StoreScopeContext);

/**
 * Routes whose default scope is "All stores" rather than "Select" — the normal
 * Orders section shows every store by default, so its picker should say so.
 */
const ALL_STORES_DEFAULT_ROUTES = ['/orders'];

/**
 * The scope to actually use, accounting for per-route defaults. Until the user
 * explicitly picks (touched), the normal Orders section defaults to All stores;
 * every other scoped page keeps the raw scope (Select). Once touched, the
 * explicit choice wins everywhere.
 */
export function useEffectiveStore(): string {
  const { store, touched, stores, storesStatus } = useStoreScope();
  const { pathname } = useLocation();
  const routeDefault = ALL_STORES_DEFAULT_ROUTES.some((r) => pathname.startsWith(r))
    ? ALL_STORES
    : NO_STORE;
  if (!touched) return routeDefault;
  return resolveStore(store, stores, storesStatus, routeDefault);
}

/**
 * A picked store that isn't (or is no longer) one of the options — deactivated,
 * unassigned, or an old name-based value — falls back to the route default
 * rather than filtering on something that matches nothing. Until the options
 * have loaded there's nothing to check against, so the pick stands.
 */
export function resolveStore(
  store: string,
  stores: StoreOption[],
  status: StoresStatus,
  routeDefault: string,
): string {
  if (store === ALL_STORES || store === NO_STORE) return store;
  if (status !== 'ready') return store;
  return stores.some((s) => s.externalId === store) ? store : routeDefault;
}

/** The `storeId` filter to send to GET /admin/orders, or undefined for no filter. */
export function storeFilterFor(store: string): string | undefined {
  return store === ALL_STORES || store === NO_STORE ? undefined : store;
}

/** Who the token belongs to — the options are role-scoped, so a new sign-in reloads them. */
function currentUserKey(): string | null {
  const token = getStoredToken();
  if (!token) return null;
  const decoded = decodeToken(token);
  return decoded?.sub ?? decoded?.email ?? token;
}

export const StoreScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [store, setStoreRaw] = useState<string>(NO_STORE);
  const [touched, setTouched] = useState(false);

  // Any explicit pick marks the scope "touched" so per-route defaults stop
  // applying and the choice sticks while navigating between scoped pages.
  const setStore = useCallback((next: string) => {
    setTouched(true);
    setStoreRaw(next);
  }, []);

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [storesStatus, setStoresStatus] = useState<StoresStatus>('idle');
  // Which user the current options (and selection) belong to, and the latest
  // request — refs so requestStores stays stable and a stale reply is dropped.
  const loadedFor = useRef<string | null>(null);
  const inFlight = useRef(false);
  const requestSeq = useRef(0);

  const requestStores = useCallback((force = false) => {
    const user = currentUserKey();
    if (!user) return;
    const sameUser = loadedFor.current === user;
    if (sameUser && (inFlight.current || !force)) return;
    if (!sameUser) {
      // A different sign-in: the last user's stores and pick don't apply.
      setStores([]);
      setStoreRaw(NO_STORE);
      setTouched(false);
    }
    loadedFor.current = user;
    inFlight.current = true;
    const seq = ++requestSeq.current;
    setStoresStatus('loading');
    authInstance
      .get<StoreOption[]>('/admin/stores/options')
      .then(({ data }) => {
        if (seq !== requestSeq.current) return;
        setStores(Array.isArray(data) ? data : []);
        setStoresStatus('ready');
      })
      .catch((error) => {
        Logger.error('[StoreScope] Failed to load store options:', error);
        if (seq === requestSeq.current) setStoresStatus('error');
      })
      .finally(() => {
        if (seq === requestSeq.current) inFlight.current = false;
      });
  }, []);

  const value = useMemo(
    () => ({ store, setStore, stores, storesStatus, requestStores, touched }),
    [store, setStore, stores, storesStatus, requestStores, touched],
  );

  return <StoreScopeContext.Provider value={value}>{children}</StoreScopeContext.Provider>;
};
